import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useT } from "../lib/i18n.jsx";
import { IconX, IconShield } from "../components/icons.jsx";
import RemoteCanvas from "../components/RemoteCanvas.jsx";

export default function BrowserConnectPage() {
  const { t } = useT();
  const { token, deviceId } = useParams();
  const navigate = useNavigate();
  const wsRef = useRef(null);

  const [status, setStatus] = useState("connecting"); // connecting | ready | streaming | closed | error
  const [statusMsg, setStatusMsg] = useState("Establishing secure connection…");
  const [sessionInfo, setSessionInfo] = useState(null);
  const [stats, setStats] = useState({ bytesIn: 0, bytesOut: 0 });
  const [remoteSize, setRemoteSize] = useState(null);
  const [openedAt] = useState(Date.now());
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const proto = window.location.protocol === "https:" ? "wss" : "ws";
    const wsUrl = `${proto}://${window.location.host}/browser-relay?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(wsUrl);
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;

    ws.onopen = () => {
      setStatusMsg("Secured. Requesting pixel stream from host…");
      // Tell backend we want to start streaming
      try {
        ws.send(JSON.stringify({ type: "browser.start", payload: {} }));
      } catch {}
    };
    ws.onmessage = (e) => {
      const sz = e.data instanceof ArrayBuffer ? e.data.byteLength : (e.data?.length || 0);
      setStats((s) => ({ ...s, bytesIn: s.bytesIn + sz }));
      if (e.data instanceof ArrayBuffer) return; // binary frames handled by RemoteCanvas
      let msg;
      try { msg = JSON.parse(e.data); } catch { return; }
      switch (msg.type) {
        case "ready":
          setSessionInfo({
            session_id: msg.session_id,
            device_id: msg.device_id,
            access_kind: msg.access_kind || "owner",
            guest_label: msg.guest_label || null,
          });
          setStatus("ready");
          setStatusMsg("Connected — requesting host to begin streaming…");
          // Now ask host to start capturing
          try {
            ws.send(JSON.stringify({ type: "browser.start", payload: {} }));
          } catch {}
          break;
        case "browser.status":
          if (msg.payload?.width && msg.payload?.height) {
            setRemoteSize({ w: msg.payload.width, h: msg.payload.height });
            setStatus("streaming");
            setStatusMsg(`Streaming ${msg.payload.width}×${msg.payload.height} at ${msg.payload.fps || 10} FPS`);
          } else {
            setStatusMsg(msg.payload?.message || "Host status update");
          }
          break;
        case "browser.error":
          setStatus("error");
          setStatusMsg(msg.payload?.message || "Host reported an error");
          break;
        case "browser.frame":
          // Handled in RemoteCanvas via its own event listener
          if (status !== "streaming") {
            setStatus("streaming");
          }
          break;
        default:
          break;
      }
    };
    ws.onerror = () => {
      setStatus("error");
      setStatusMsg("Connection error");
    };
    ws.onclose = (e) => {
      // Tell host to stop streaming
      try { ws.send(JSON.stringify({ type: "browser.stop", payload: {} })); } catch {}
      setStatus("closed");
      setStatusMsg(`Disconnected${e.reason ? " — " + e.reason : ""} (code ${e.code})`);
    };

    return () => {
      try {
        ws.send(JSON.stringify({ type: "browser.stop", payload: {} }));
      } catch {}
      try { ws.close(1000, "page left"); } catch {}
    };
  }, [token]);

  function sendInput(type, payload) {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== 1) return;
    const msg = JSON.stringify({ type, payload });
    ws.send(msg);
    setStats((s) => ({ ...s, bytesOut: s.bytesOut + msg.length }));
  }

  function disconnect() {
    try { wsRef.current?.close(1000, "user disconnect"); } catch {}
    navigate("/devices");
  }

  const elapsedSec = Math.floor((now - openedAt) / 1000);
  const m = Math.floor(elapsedSec / 60);
  const s = elapsedSec % 60;

  const statusColor = {
    connecting: "var(--warn)",
    ready: "var(--ok)",
    closed: "var(--text-dim)",
    error: "var(--err)",
  }[status];

  return (
    <div style={{
      minHeight: "100vh",
      background: "#000",
      display: "flex",
      flexDirection: "column",
    }}>
      {/* Top status bar */}
      <header style={{
        padding: "10px 18px",
        background: "var(--bg-elev)",
        borderBottom: "1px solid var(--line)",
        display: "flex",
        alignItems: "center",
        gap: 18,
        flexWrap: "wrap",
      }}>
        <div className="brand-mark" style={{ fontSize: 14 }}>
          vorks<span className="dot" /><span className="con">connect</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{
            width: 8, height: 8, borderRadius: "50%",
            background: statusColor,
            boxShadow: status === "ready" ? `0 0 8px ${statusColor}` : "none",
            animation: status === "connecting" ? "pulse-2 1s ease-in-out infinite" : "none",
          }} />
          <span className="mono" style={{ fontSize: 11, color: statusColor, letterSpacing: "0.1em", textTransform: "uppercase" }}>
            {status}
          </span>
        </div>
        {sessionInfo?.access_kind && sessionInfo.access_kind !== "owner" && (
          <AccessKindBadge kind={sessionInfo.access_kind} label={sessionInfo.guest_label} />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: 13, color: "var(--text-mute)" }}>{statusMsg}</span>
        </div>
        <div className="mono" style={{ fontSize: 11, color: "var(--text-faint)" }}>
          {m}:{String(s).padStart(2, "0")} · ↓{(stats.bytesIn/1024).toFixed(1)}KB · ↑{(stats.bytesOut/1024).toFixed(1)}KB
        </div>
        <button className="btn ghost danger" onClick={disconnect} style={{ padding: "6px 12px" }}>
          <IconX size={12} /> Disconnect
        </button>
      </header>

      {/* Main viewport */}
      <main style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
      }}>
        {status === "connecting" && <Loading message={statusMsg} />}
        {status === "ready" && !remoteSize && (
          <Loading message={statusMsg || "Waiting for host to begin streaming…"} />
        )}
        {status === "streaming" && remoteSize && (
          <RemoteCanvas
            ws={wsRef.current}
            sendInput={sendInput}
            remoteWidth={remoteSize.w}
            remoteHeight={remoteSize.h}
          />
        )}
        {(status === "closed" || status === "error") && (
          <Closed status={status} message={statusMsg} onBack={() => navigate("/devices")} />
        )}
      </main>
    </div>
  );
}

function Loading({ message }) {
  return (
    <div style={{ textAlign: "center", padding: 40 }}>
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 1.4, repeat: Infinity, ease: "linear" }}
        style={{
          width: 36, height: 36,
          border: "2px solid var(--line)",
          borderTopColor: "var(--acc)",
          borderRadius: "50%",
          margin: "0 auto 18px",
        }}
      />
      <div className="mono" style={{ fontSize: 11, color: "var(--text-dim)", letterSpacing: "0.15em", textTransform: "uppercase" }}>
        {message}
      </div>
    </div>
  );
}

function ViewportPlaceholder({ sessionInfo, deviceId, onTestInput }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      style={{
        textAlign: "center",
        padding: 40,
        maxWidth: 540,
      }}
    >
      <div style={{
        width: 64, height: 64,
        margin: "0 auto 20px",
        borderRadius: "50%",
        border: "1px solid var(--acc)",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "var(--acc)",
        boxShadow: "0 0 24px var(--acc-glow)",
      }}>
        <IconShield size={28} />
      </div>
      <div className="eyebrow" style={{ alignSelf: "center" }}>secure pipe established</div>
      <h1 className="serif italic-serif" style={{ fontSize: 32, color: "var(--text)", margin: "12px 0 16px", lineHeight: 1.2 }}>
        Ready to receive pixels
      </h1>
      <p className="dim" style={{ fontSize: 14, lineHeight: 1.6 }}>
        The control plane is connected to your host. The encrypted relay is open,
        2FA verified, IP pinned. In <strong style={{ color: "var(--text)" }}>Block D2</strong> this
        view becomes the live remote desktop.
      </p>

      <div style={{
        marginTop: 32,
        padding: "16px 20px",
        background: "var(--bg-elev)",
        border: "1px solid var(--line)",
        borderRadius: "var(--r-lg)",
        textAlign: "left",
      }}>
        <div className="mono" style={{ fontSize: 10, color: "var(--text-faint)", letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 8 }}>
          Session
        </div>
        <div style={{ fontSize: 12, color: "var(--text-mute)", lineHeight: 1.6 }}>
          <div>Device: <span className="mono" style={{ color: "var(--text)" }}>{deviceId}</span></div>
          <div>Session ID: <span className="mono" style={{ color: "var(--text)" }}>{sessionInfo?.session_id}</span></div>
        </div>
      </div>

      <button className="btn ghost" style={{ marginTop: 18 }} onClick={onTestInput}>
        Send test ping to host
      </button>
    </motion.div>
  );
}

function Closed({ status, message, onBack }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{ textAlign: "center", padding: 40, maxWidth: 480 }}
    >
      <div className="eyebrow">{status === "error" ? "Error" : "Disconnected"}</div>
      <h2 style={{ fontSize: 24, marginTop: 8, color: status === "error" ? "var(--err)" : "var(--text)" }}>
        {status === "error" ? "Connection failed" : "Session ended"}
      </h2>
      <p className="dim" style={{ marginTop: 12, fontSize: 13 }}>{message}</p>
      <button className="btn primary" onClick={onBack} style={{ marginTop: 20 }}>
        Back to devices
      </button>
    </motion.div>
  );
}

function AccessKindBadge({ kind, label }) {
  const styles = {
    guest:  { bg: "rgba(234,179,8,0.12)",  border: "rgba(234,179,8,0.4)",  fg: "#facc15", text: "GUEST" },
    share:  { bg: "rgba(168,85,247,0.12)", border: "rgba(168,85,247,0.4)", fg: "#c084fc", text: "SHARED" },
    admin:  { bg: "rgba(244,114,182,0.12)", border: "rgba(244,114,182,0.4)", fg: "#f472b6", text: "ADMIN" },
  }[kind] || { bg: "rgba(255,255,255,0.06)", border: "var(--line)", fg: "var(--text-dim)", text: kind?.toUpperCase() };

  return (
    <div
      title={label || `Connection type: ${styles.text.toLowerCase()}`}
      style={{
        display: "flex", alignItems: "center", gap: 6,
        padding: "3px 9px", borderRadius: 4,
        background: styles.bg,
        border: `1px solid ${styles.border}`,
        color: styles.fg,
        fontSize: 10,
        fontFamily: "var(--mono)",
        letterSpacing: "0.1em",
        whiteSpace: "nowrap",
      }}
    >
      {styles.text}
      {label && (
        <span style={{ color: "var(--text-dim)", fontWeight: 400, fontSize: 10 }}>
          · {label}
        </span>
      )}
    </div>
  );
}
